import React, { useEffect, useState } from "react";
import { FileDown, FileText, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { api, getMl, fileUrl } from "@/lib/api";

const COUNTRY_CODES = [
  { code: "+62", label: "🇮🇩 +62 Indonesia" },
  { code: "+90", label: "🇹🇷 +90 Türkiye" },
  { code: "+65", label: "🇸🇬 +65 Singapore" },
  { code: "+60", label: "🇲🇾 +60 Malaysia" },
  { code: "+66", label: "🇹🇭 +66 Thailand" },
  { code: "+63", label: "🇵🇭 +63 Philippines" },
  { code: "+971", label: "🇦🇪 +971 UAE" },
  { code: "+966", label: "🇸🇦 +966 Saudi Arabia" },
  { code: "+974", label: "🇶🇦 +974 Qatar" },
  { code: "+91", label: "🇮🇳 +91 India" },
  { code: "+92", label: "🇵🇰 +92 Pakistan" },
  { code: "+86", label: "🇨🇳 +86 China" },
  { code: "+81", label: "🇯🇵 +81 Japan" },
  { code: "+82", label: "🇰🇷 +82 South Korea" },
  { code: "+44", label: "🇬🇧 +44 UK" },
  { code: "+49", label: "🇩🇪 +49 Germany" },
  { code: "+33", label: "🇫🇷 +33 France" },
  { code: "+31", label: "🇳🇱 +31 Netherlands" },
  { code: "+1", label: "🇺🇸 +1 USA/Canada" },
];

const CATALOG_TEXT = {
  en: {
    subtitle: "Download company profile, product and commodity catalogues, and service brochures.",
    request: "Request Access",
    modalTitle: "Request Catalogue Access",
    modalSub: "Please share your details so our trade desk can prepare a tailored follow-up.",
    name: "Full Name", company: "Company", email: "Business Email",
    phoneCode: "Country Code", phone: "Phone Number", country: "Country",
    submit: "Get Catalogue", success: "Thanks! Opening your catalogue now.",
    required: "All fields are required.",
  },
  tr: {
    subtitle: "Şirket profili, ürün ve emtia kataloglarını ve hizmet broşürlerini indirin.",
    request: "Erişim İste",
    modalTitle: "Katalog Erişim Talebi",
    modalSub: "Ticaret masamızın size özel takip hazırlaması için lütfen bilgilerinizi paylaşın.",
    name: "Ad Soyad", company: "Şirket", email: "İş E-postası",
    phoneCode: "Ülke Kodu", phone: "Telefon", country: "Ülke",
    submit: "Kataloğu Al", success: "Teşekkürler! Kataloğunuz açılıyor.",
    required: "Tüm alanlar zorunludur.",
  },
  id: {
    subtitle: "Unduh profil perusahaan, katalog produk dan komoditas, serta brosur layanan.",
    request: "Minta Akses",
    modalTitle: "Permintaan Akses Katalog",
    modalSub: "Bagikan detail Anda agar tim kami dapat menyiapkan tindak lanjut yang sesuai.",
    name: "Nama Lengkap", company: "Perusahaan", email: "Email Bisnis",
    phoneCode: "Kode Negara", phone: "Nomor Telepon", country: "Negara",
    submit: "Dapatkan Katalog", success: "Terima kasih! Katalog Anda dibuka.",
    required: "Semua kolom wajib diisi.",
  },
};

const CatalogueCenter = () => {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const T = CATALOG_TEXT[lang] || CATALOG_TEXT.en;

  useEffect(() => {
    api.get("/catalogues").then((r) => setItems(r.data));
  }, []);

  const titles = { en: "Catalogue Center", tr: "Katalog Merkezi", id: "Pusat Katalog" }[lang];

  return (
    <div data-testid="catalogue-page">
      <section className="bg-[#0A1128] text-white py-24 md:py-28">
        <div className="container-nakia">
          <div className="flex items-center gap-3 mb-5">
            <span className="gold-divider" />
            <span className="text-eyebrow text-[#D4AF37]">PDF Library</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl">{titles}</h1>
          <p className="text-white/75 max-w-2xl mt-6">{T.subtitle}</p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-nakia">
          {items.length === 0 ? (
            <p className="text-center text-slate-500 py-20">{t("common.noResults")}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((c) => (
                <div key={c.id} data-testid={`catalogue-${c.id}`} className="card-light overflow-hidden">
                  <div className="aspect-[4/5] bg-[#0A1128] flex items-center justify-center relative">
                    {c.cover_image ? (
                      <img src={fileUrl(c.cover_image)} alt="" className="w-full h-full object-cover opacity-90" />
                    ) : (
                      <FileText size={64} className="text-[#D4AF37]/60" />
                    )}
                  </div>
                  <div className="p-7">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">{c.category}</div>
                    <h3 className="font-display text-xl text-[#0A1128] mb-2">{getMl(c.title, lang)}</h3>
                    <p className="text-sm text-slate-600 mb-5 line-clamp-3">{getMl(c.description, lang)}</p>
                    <button
                      onClick={() => setActive(c)}
                      data-testid={`catalogue-request-${c.id}`}
                      className="btn-outline-navy w-full"
                    >
                      <FileDown size={14} /> {T.request}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {active && (
        <CatalogueRequestModal
          catalogue={active}
          lang={lang}
          T={T}
          onClose={() => setActive(null)}
        />
      )}
    </div>
  );
};

const CatalogueRequestModal = ({ catalogue, lang, T, onClose }) => {
  const [form, setForm] = useState({
    name: "", company: "", email: "",
    phone_country_code: "+62", phone_number: "", country: "",
  });
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    // Client-side check
    for (const k of ["name", "company", "email", "phone_country_code", "phone_number", "country"]) {
      if (!form[k]?.trim()) {
        toast.error(T.required);
        return;
      }
    }
    setLoading(true);
    try {
      const res = await api.post("/catalogue-requests", {
        catalogue_id: catalogue.id,
        ...form,
      });
      toast.success(T.success);
      onClose();
      // Open PDF in new tab
      if (res.data.pdf_url) {
        window.open(fileUrl(res.data.pdf_url), "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" data-testid="catalogue-request-modal">
      <form onSubmit={submit} className="bg-white max-w-lg w-full rounded-sm">
        <div className="flex justify-between items-start p-6 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">{catalogue.category}</div>
            <h3 className="font-display text-2xl text-[#0A1128]">{T.modalTitle}</h3>
            <p className="text-sm text-slate-500 mt-1.5 max-w-md">{T.modalSub}</p>
            <p className="text-xs text-slate-400 mt-3">
              <span className="text-[#0A1128] font-medium">{getMl(catalogue.title, lang)}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="close-request-modal" className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          <Field label={T.name} v={form.name} on={change("name")} testId="req-name" required />
          <Field label={T.company} v={form.company} on={change("company")} testId="req-company" required />
          <Field label={T.email} type="email" v={form.email} on={change("email")} testId="req-email" required />

          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">
              {T.phone} <span className="text-[#D4AF37]">*</span>
            </label>
            <div className="grid grid-cols-5 gap-2">
              <select
                value={form.phone_country_code}
                onChange={change("phone_country_code")}
                data-testid="req-phone-code"
                required
                className="col-span-2 px-3 py-3 border border-slate-300 rounded-sm text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <input
                type="tel"
                inputMode="tel"
                pattern="[0-9\s-]{5,}"
                value={form.phone_number}
                onChange={change("phone_number")}
                placeholder="812 3456 7890"
                required
                data-testid="req-phone-number"
                className="col-span-3 px-4 py-3 border border-slate-300 rounded-sm text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              />
            </div>
          </div>

          <Field label={T.country} v={form.country} on={change("country")} testId="req-country" required />

          <p className="text-[10px] text-slate-400 leading-relaxed pt-2 border-t border-slate-100">
            By submitting, you agree to be contacted by Nakia Global Sejahtera regarding this catalogue and related trade solutions.
          </p>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.2em] text-slate-500 px-5">Cancel</button>
          <button type="submit" disabled={loading} data-testid="req-submit" className="btn-gold disabled:opacity-50">
            {loading ? <><Loader2 size={14} className="animate-spin" /> …</> : <><Send size={14} /> {T.submit}</>}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, v, on, type = "text", required, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
    <input
      type={type}
      value={v}
      onChange={on}
      required={required}
      data-testid={testId}
      className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
    />
  </div>
);

export default CatalogueCenter;
